"use client"

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { BillingTransactionsContent } from './BillingTransactionsContent'
import { useBillingTransactionReports, ReportSearchParams } from '@/hooks/use-billing-transaction-reports'
import { useCodeValues } from '@/hooks/use-code-values'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Search, Filter, RefreshCw, X, Download } from 'lucide-react'
import { TablePagination } from '@/components/ui/table-pagination'
import { DatePicker } from '@/components/ui/date-picker'
import { MultiSelect } from '@/components/ui/multi-select'
import { formatDateOnly } from '@/lib/adapters/engagement-adapter'

const SORT_ORDER_OPTIONS = [
  { value: 'BillingDate desc, EntityId desc', label: 'Billing Date (Newest)' },
  { value: 'BillingDate asc, EntityId asc', label: 'Billing Date (Oldest)' },
  { value: 'BillingTransactionNumber desc', label: 'Transaction # (Newest)' },
  { value: 'BillingTransactionNumber asc', label: 'Transaction # (Oldest)' },
  { value: 'TotalAmount desc', label: 'Amount (Highest)' },
  { value: 'TotalAmount asc', label: 'Amount (Lowest)' },
  { value: 'BillingTransactionStatusId asc', label: 'Billing Status' },
  { value: 'InvoiceStatusId asc', label: 'Invoice Status' },
]

const DEFAULT_SORT = { field: 'BillingDate', direction: 'desc' as const }

interface BillingTransactionsSectionProps {
  engagementId?: number
}

export function BillingTransactionsSection({ engagementId }: BillingTransactionsSectionProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [billingStatusFilter, setBillingStatusFilter] = useState<number[]>([])
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<number[]>([])
  const [startDateFilter, setStartDateFilter] = useState<Date | null>(null)
  const [endDateFilter, setEndDateFilter] = useState<Date | null>(null)
  const [sortOrder, setSortOrder] = useState<{ field: string; direction: 'asc' | 'desc' }>(DEFAULT_SORT)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)

  // Get code values for status filters
  const { getCodeValues } = useCodeValues()
  const billingStatusOptions = getCodeValues('trn.CodeBillingTransactionStatus', false)
  const invoiceStatusOptions = getCodeValues('inv.CodeInvoiceStatus', false)

  const hasActiveFilters =
    searchTerm !== '' ||
    billingStatusFilter.length > 0 ||
    invoiceStatusFilter.length > 0 ||
    startDateFilter !== null ||
    endDateFilter !== null ||
    sortOrder.field !== DEFAULT_SORT.field ||
    sortOrder.direction !== DEFAULT_SORT.direction

  // Toggle sort on a column: same field toggles direction, new field defaults to desc
  const handleSort = useCallback((field: string) => {
    setSortOrder(prev =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'desc' }
    )
  }, [])

  // Resolve the OData $orderby string
  const resolvedOrderBy = useMemo(() => {
    const match = SORT_ORDER_OPTIONS.find(opt =>
      opt.value.startsWith(`${sortOrder.field} ${sortOrder.direction}`)
    )
    return match?.value ?? `${sortOrder.field} ${sortOrder.direction}`
  }, [sortOrder])

  // Build composite OData $filter and $search
  const searchParams: ReportSearchParams = useMemo(() => {
    const filters: string[] = []

    if (billingStatusFilter.length > 0) {
      filters.push(`(${billingStatusFilter.map(id => `BillingTransactionStatusId eq ${id}`).join(' or ')})`)
    }
    if (invoiceStatusFilter.length > 0) {
      filters.push(`(${invoiceStatusFilter.map(id => `InvoiceStatusId eq ${id}`).join(' or ')})`)
    }
    if (startDateFilter) {
      filters.push(`BillingDate ge ${formatDateOnly(startDateFilter)}`)
    }
    if (endDateFilter) {
      filters.push(`BillingDate le ${formatDateOnly(endDateFilter)}`)
    }

    return {
      search: searchTerm || undefined,
      filter: filters.length > 0 ? filters.join(' and ') : undefined,
      orderBy: resolvedOrderBy,
    }
  }, [searchTerm, billingStatusFilter, invoiceStatusFilter, startDateFilter, endDateFilter, resolvedOrderBy])

  const {
    billingTransactions,
    loading,
    error,
    totalCount,
    isExporting,
    exportError,
    refetch,
    exportToCSV
  } = useBillingTransactionReports({
    engagementId,
    autoFetch: true,
    searchParams,
    currentPage,
    pageSize
  })

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, billingStatusFilter, invoiceStatusFilter, startDateFilter, endDateFilter, resolvedOrderBy])

  const handleSearch = () => {
    if (currentPage === 1) {
      refetch()
    } else {
      setCurrentPage(1)
    }
  }

  const handleRefresh = () => {
    if (currentPage === 1) {
      refetch()
    } else {
      setCurrentPage(1)
    }
  }

  const handleExport = () => {
    exportToCSV(searchParams)
  }

  const handleClearFilters = () => {
    setSearchTerm('')
    setBillingStatusFilter([])
    setInvoiceStatusFilter([])
    setStartDateFilter(null)
    setEndDateFilter(null)
    setSortOrder(DEFAULT_SORT)
  }

  return (
    <Card className="border w-full">
      <CardHeader className="border-b border-slate-200">
        <div className="flex flex-col space-y-4">
          {/* Header Row */}
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl font-semibold text-slate-900">
              All Items ({totalCount})
              {loading && (
                <span className="ml-2 text-sm font-normal text-slate-500">Loading...</span>
              )}
            </CardTitle>
            <div className="flex items-center gap-3">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-slate-100"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
                {loading && (
                  <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              <Button
                onClick={handleRefresh}
                disabled={loading}
                variant="outline"
                size="sm"
                className="border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="border-slate-300 hover:bg-slate-50"
              >
                <Filter className="mr-2 h-4 w-4" />
                {showFilters ? "Hide Filters" : "Filters"}
              </Button>
              {/* Clear Filters — shown whenever any filter is non-default */}
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="border-slate-300 hover:bg-slate-50"
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear Filters
                </Button>
              )}
              <Button
                onClick={handleExport}
                disabled={isExporting || loading}
                variant="outline"
                size="sm"
                className="border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                title={isExporting ? "Exporting..." : "Export CSV"}
              >
                <Download className={`h-4 w-4 ${isExporting ? 'animate-pulse' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Export Error */}
          {exportError && (
            <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">Export failed: {exportError}</p>
            </div>
          )}

          {/* Advanced Filters — Billing Status, Invoice Status, Start Date, End Date */}
          {showFilters && (
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Billing Status Filter (multi-select) */}
                <MultiSelect
                  options={billingStatusOptions}
                  value={billingStatusFilter}
                  onChange={setBillingStatusFilter}
                  placeholder="All Billing Statuses"
                />

                {/* Invoice Status Filter (multi-select) */}
                <MultiSelect
                  options={invoiceStatusOptions}
                  value={invoiceStatusFilter}
                  onChange={setInvoiceStatusFilter}
                  placeholder="All Invoice Statuses"
                />

                {/* Start Date */}
                <DatePicker
                  value={startDateFilter}
                  onChange={setStartDateFilter}
                  placeholder="Start date"
                  maxDate={endDateFilter || undefined}
                />

                {/* End Date */}
                <DatePicker
                  value={endDateFilter}
                  onChange={setEndDateFilter}
                  placeholder="End date"
                  minDate={startDateFilter || undefined}
                />
              </div>
            </div>
          )}
        </div>
      </CardHeader>

      {/* Billing Transactions Table Content */}
      <CardContent className="p-0">
        <BillingTransactionsContent
          billingTransactions={billingTransactions}
          loading={loading}
          error={error}
          sortField={sortOrder.field}
          sortDirection={sortOrder.direction}
          onSort={handleSort}
        />

        {/* Pagination */}
        <TablePagination
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          disabled={loading}
        />
      </CardContent>
    </Card>
  )
}
